import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  getDomainConfig,
  getShortPaymentPath,
  getPaymentReportPath,
  type RedirectType,
} from '../config/redirect.config';

@Injectable({
  providedIn: 'root',
})
export class DomainRedirectService {
  private router = inject(Router);

  /**
   * Check current hostname and perform redirect if configured
   * @returns true if redirect was performed, false otherwise
   */
  checkAndRedirect(): boolean {
    const hostname = window.location.hostname;
    const currentPath = window.location.pathname;

    // Check if hostname is configured
    const domainConfig = getDomainConfig(hostname);
    if (!domainConfig) {
      return false;
    }

    // Check if already on correct path
    if (this.isOnCorrectPath(currentPath, domainConfig.shortId)) {
      return false; // No redirect needed
    }

    // Determine redirect type based on current path
    const redirectType = this.determineRedirectType(currentPath);

    if (redirectType === 'payment-report') {
      const paymentReportPath = getPaymentReportPath(hostname);
      if (paymentReportPath) {
        this.router.navigate([paymentReportPath]);
        return true;
      }
    } else {
      const shortPaymentPath = getShortPaymentPath(hostname);
      if (shortPaymentPath) {
        this.router.navigate([shortPaymentPath]);
        return true;
      }
    }

    return false;
  }

  /**
   * Check if the current path is already the correct path for this domain
   * @param currentPath - The current path
   * @param shortId - The domain's short ID
   * @returns true if already on correct path, false otherwise
   */
  private isOnCorrectPath(currentPath: string, shortId: string): boolean {
    const correctShortPath = `/s/${shortId}`;
    const correctReportPath = `/payment-report/${shortId}`;

    return currentPath === correctShortPath || currentPath === correctReportPath;
  }

  /**
   * Determine the redirect type based on the current path
   * @param currentPath - The current path
   * @returns The redirect type
   */
  private determineRedirectType(currentPath: string): RedirectType {
    if (currentPath.includes('payment-report')) {
      return 'payment-report';
    }
    return 'short-payment';
  }

  /**
   * Get redirect path for a specific type
   * @param hostname - The hostname to get path for
   * @param redirectType - The type of redirect
   * @returns The redirect path if found, null otherwise
   */
  getRedirectPathByType(hostname: string, redirectType: RedirectType): string | null {
    if (redirectType === 'payment-report') {
      return getPaymentReportPath(hostname);
    } else {
      return getShortPaymentPath(hostname);
    }
  }

  /**
   * Check if a hostname is configured for redirects
   * @param hostname - The hostname to check
   * @returns true if the hostname is configured, false otherwise
   */
  isConfigured(hostname: string): boolean {
    return getDomainConfig(hostname) !== null;
  }

  /**
   * Get short payment path for a hostname
   * @param hostname - The hostname to get path for
   * @returns The short payment path if found, null otherwise
   */
  getShortPaymentPath(hostname: string): string | null {
    return getShortPaymentPath(hostname);
  }

  /**
   * Get payment report path for a hostname
   * @param hostname - The hostname to get path for
   * @returns The payment report path if found, null otherwise
   */
  getPaymentReportPath(hostname: string): string | null {
    return getPaymentReportPath(hostname);
  }
}
